import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { recoverAdminAccount, checkAdminAccountStatus } from '../services/admin/adminRecoveryService';

const AdminRecovery = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [accountFound, setAccountFound] = useState(false);
  const navigate = useNavigate();

  const handleCheck = async () => {
    if (!email) {
      setError('Veuillez entrer votre adresse email.');
      return;
    }

    setError('');
    setSuccess('');
    setChecking(true);

    try {
      const result = await checkAdminAccountStatus(email);
      
      if (result.exists && result.isAdmin) {
        setAccountFound(true);
        setSuccess('Compte administrateur trouvé. Veuillez définir un nouveau mot de passe pour récupérer l\'accès.');
      } else if (result.exists && !result.isAdmin) {
        setError('Ce compte existe mais n\'est pas un compte administrateur.');
      } else {
        setError('Aucun compte administrateur trouvé avec cet email.');
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du compte:', error);
      setError('Une erreur est survenue lors de la vérification du compte.');
    } finally {
      setChecking(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Tous les champs sont obligatoires.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const result = await recoverAdminAccount(email, password);
      
      if (result.success) {
        setSuccess(result.message);
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/admin-login');
        }, 3000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du compte:', error);
      setError('Une erreur est survenue lors de la récupération du compte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      backgroundColor: '#f5f5f5'
    }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        width: '100%', 
        maxWidth: '400px',
        borderRadius: '10px'
      }}>
        <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#014527' }}>
          Récupération de Compte Administrateur
        </Typography>
        
        <Typography variant="body2" gutterBottom align="center" sx={{ mb: 3 }}>
          Ce formulaire permet de récupérer un compte administrateur qui existe dans la base de données mais pas dans le système d'authentification.
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {!accountFound ? (
          <Box>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              disabled={checking}
            />
            
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCheck}
              disabled={checking}
              sx={{ 
                mt: 2, 
                mb: 2,
                backgroundColor: '#014527',
                '&:hover': {
                  backgroundColor: '#01321c',
                }
              }}
            >
              {checking ? <CircularProgress size={24} color="inherit" /> : 'Vérifier le compte'}
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleRecover}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              disabled
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Nouveau mot de passe"
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Confirmer le mot de passe"
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ 
                mt: 2, 
                mb: 2,
                backgroundColor: '#014527',
                '&:hover': {
                  backgroundColor: '#01321c',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Récupérer le compte'}
            </Button>
          </form>
        )}
        
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2">
            <Link to="/admin-login" style={{ color: '#014527', textDecoration: 'none' }}>
              Retour à la connexion
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminRecovery;